import React, { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useMatch } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../services/axiosInstance";
import { authorizeOrder } from "../../services/bookings";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  Button,
  HStack,
} from "@chakra-ui/react";

const extractPaymentError = (error, fallback) =>
  error?.response?.data?.error || fallback;

const Checkout = ({ showPreviousButton, onPaypalOrderCreated }) => {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const match = useMatch("/book/:bookingId");
  const bookingId = match?.params.bookingId;

  const invalidateBookingQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["booking-unique", bookingId] });
    queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
  };

  const clearPaymentMessages = () => {
    setPaymentError(null);
    setPaymentInfo(null);
  };

  const createOrder = () => {
    clearPaymentMessages();
    return axiosInstance
      .post(`/bookings/${bookingId}/create_paypal_order`)
      .then((response) => {
        onPaypalOrderCreated?.();
        return response.data.orderId;
      })
      .catch((error) => {
        const message = extractPaymentError(
          error,
          t("bookingPaymentGenericError")
        );
        setPaymentError(message);
        throw error;
      });
  };

  const onApprove = async (data) => {
    setProcessing(true);
    clearPaymentMessages();
    try {
      const response = await authorizeOrder(bookingId, data.orderID);
      if (response.status === 200) {
        invalidateBookingQueries();
        setSuccess(true);
      } else {
        setPaymentError(t("bookingPaymentGenericError"));
      }
    } catch (error) {
      setPaymentError(
        extractPaymentError(error, t("bookingPaymentGenericError"))
      );
    } finally {
      setProcessing(false);
    }
  };

  const onError = () => {
    setProcessing(false);
    setPaymentError(t("bookingPaymentGenericError"));
  };

  const onCancel = () => {
    setProcessing(false);
    setPaymentInfo(t("bookingPaymentCancelled"));
  };

  useEffect(() => {
    if (success) {
      showPreviousButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <PayPalScriptProvider
      options={{
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
        currency: "EUR",
        intent: "authorize",
      }}
    >
      {paymentError && (
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          <VStack align="flex-start" spacing={2} flex="1">
            <AlertTitle fontSize="md">{t("bookingPaymentErrorTitle")}</AlertTitle>
            <AlertDescription fontSize="sm">
              {paymentError || t("bookingPaymentErrorDescription")}
            </AlertDescription>
            <HStack pt={1}>
              <Button size="sm" colorScheme="red" variant="outline" onClick={clearPaymentMessages}>
                {t("bookingPaymentTryAgain")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.location.reload()}>
                {t("bookingPaymentReloadPage")}
              </Button>
            </HStack>
          </VStack>
        </Alert>
      )}

      {paymentInfo && !paymentError && (
        <Alert status="info" borderRadius="lg" mb={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{paymentInfo}</AlertDescription>
        </Alert>
      )}

      {!success && !processing && !paymentError && (
        <div>
          <br />
          <PayPalButtons
            style={{ layout: "vertical" }}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={onError}
            onCancel={onCancel}
          />
        </div>
      )}

      {processing && (
        <div>
          <Alert
            borderRadius="lg"
            status="info"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {t("bookingPaymentProcessing")}
            </AlertTitle>
            <AlertDescription fontSize="sm">
              {t("bookingPaymentProcessingDescription")}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {success && (
        <div>
          <VStack>
            <Alert
              borderRadius="lg"
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              maxHeight="400px"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Reservation completed!
              </AlertTitle>
              <AlertDescription fontSize="sm">
                Thanks for submitting your reservation. Our team will get back
                to you soon via email and WhatsApp.
              </AlertDescription>
            </Alert>

            <Alert
              borderRadius="lg"
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              maxHeight="400px"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Reservation confirmation
              </AlertTitle>
              <AlertDescription fontSize="sm" flexWrap="wrap">
                A 20% deposit of the total amount is currently pending
                confirmation. If the reservation cannot be confirmed, the deposit
                will be automatically refunded. Please stay tuned for an email
                and WhatsApp message with further updates.
              </AlertDescription>
            </Alert>
          </VStack>
        </div>
      )}
    </PayPalScriptProvider>
  );
};

export default Checkout;
